import type { CutsceneOutcome } from '@sorrem-client/ui/features/Cutscene';
import { useCrucibleCutscenes } from '@sorrem-client/ui/hooks';
import type { CrucibleRecord } from '@sorrem/shared';
import { useCallback, useState } from 'react';

export interface BeforeCutsceneState {
    /** The cutscene to play now, or null. */
    beforeCutsceneId: string | null;
    /** The cutscene is known and done with (or there is none): the run may start. */
    isSettled: boolean;
    /** The cutscene ended: marks it seen unless it failed to load; the gathering goes on. */
    finish: (outcome: CutsceneOutcome) => void;
}

/**
 * The run's before-cutscene, which plays over the gathering screen, and only
 * while the run hasn't started: arriving at a run already under way (a
 * reload, a late party member) goes straight on. Watching or skipping it
 * marks it seen.
 */
export function useBeforeCutscene(instanceId: string | null, crucible: CrucibleRecord | null): BeforeCutsceneState {
    const { pending, isLoading, markSeen } = useCrucibleCutscenes(instanceId);
    const [hasFinished, setHasFinished] = useState(false);
    const beforeCutsceneId = hasFinished || crucible === null || crucible.startedAt !== null
        ? null
        : pending?.before ?? null;
    const isSettled = !isLoading && beforeCutsceneId === null;

    const finish = useCallback((outcome: CutsceneOutcome): void => {
        if (beforeCutsceneId !== null && outcome !== 'failed') {
            void markSeen(beforeCutsceneId);
        }
        setHasFinished(true);
    }, [beforeCutsceneId, markSeen]);

    return { beforeCutsceneId, isSettled, finish };
}
